import type {
  IndividualPaymentOrderTrackingViewModel,
  IndividualPaymentTraceabilityViewModel,
} from '@/shared/api/generated';
import {
  useGetApiV1IndividualpaymentFinancialPeopleFinancialpersonidOrderTracking,
  useGetApiV1IndividualpaymentFinancialPeopleFinancialpersonidTraceability,
} from '@/shared/api/generated';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';

import type { PaymentHistoryMode, PaymentHistoryResult } from '../types/paymentHistory';

/**
 * Histórico de um plano financeiro — auditoria campo a campo ou rastreio de
 * pedidos, conforme o modo.
 *
 * Espelha `useParcelHistory`: mesma forma de retorno, mesmo desenho de `enabled`.
 * O que muda é só o escopo da URL — `financial-people/{financialPersonId}` em vez
 * de `parcels/{parcelId}` — porque os dois pares de endpoints devolvem os mesmos
 * ViewModels.
 *
 * `traceability` é o log de auditoria do plano (`GA_TRACEABILITY`, entidade
 * `CourseFinancialPerson`), exposto na tela como "Rastreamento de operações";
 * `orderTracking` é `GA_FINANCIAL_ORDER_LOG`, exposto como "Rastreamento de
 * pedidos". Os rótulos vêm do legado e não se parecem com os nomes dos endpoints.
 *
 * @param financialPersonId - ID do contrato financeiro; `null` desabilita as duas queries.
 * @param mode - Histórico a carregar; `null` quando o painel está fechado.
 *
 * @example
 * ```tsx
 * const { orderTracking, isLoading } = usePlanHistory(financialPersonId, 'orderTracking');
 * ```
 */
export function usePlanHistory(
  financialPersonId: number | null,
  mode: PaymentHistoryMode | null,
): PaymentHistoryResult {
  // Assinaturas diferentes entre os dois hooks gerados: só o de order-tracking
  // tem query params (`culture`, com `pt-BR` como default do backend), então nele
  // as opções de query vão no terceiro argumento.
  const traceabilityQuery =
    useGetApiV1IndividualpaymentFinancialPeopleFinancialpersonidTraceability(
      financialPersonId ?? 0,
      { query: { enabled: financialPersonId !== null && mode === 'traceability' } },
    );

  const orderTrackingQuery =
    useGetApiV1IndividualpaymentFinancialPeopleFinancialpersonidOrderTracking(
      financialPersonId ?? 0,
      undefined,
      { query: { enabled: financialPersonId !== null && mode === 'orderTracking' } },
    );

  const active = mode === 'orderTracking' ? orderTrackingQuery : traceabilityQuery;

  return {
    traceability: unwrapPagedResponse<IndividualPaymentTraceabilityViewModel>(
      traceabilityQuery.data,
    ),
    orderTracking: unwrapPagedResponse<IndividualPaymentOrderTrackingViewModel>(
      orderTrackingQuery.data,
    ),
    isLoading: mode !== null && active.isLoading,
    isError: active.isError,
    refetch: active.refetch,
  } as const;
}
